/**
 * Per-message reasoning effort. The value picks the model the AI server uses,
 * so it directly changes both answer quality and how fast the plan's AI
 * allowance is consumed.
 *
 * Persisted so the choice survives a refresh — it is a working preference, not
 * a per-message decision most users want to re-make every time.
 *
 * The server is the authority: it re-validates the value and pins token-metered
 * (free) plans to low regardless of what is sent.
 */
export type ChatEffort = 'low' | 'medium' | 'high'

export const CHAT_EFFORTS: ChatEffort[] = ['low', 'medium', 'high']

export const DEFAULT_EFFORT: ChatEffort = 'medium'

/**
 * The only tier a free plan may actually run. Free plans meter tokens rather
 * than spend, so the server pins them here — the picker still lists the higher
 * tiers, marked locked, as the upgrade path.
 */
export const FREE_PLAN_EFFORT: ChatEffort = 'low'

export const EFFORT_LABELS: Record<ChatEffort, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
}

export const EFFORT_DESCRIPTIONS: Record<ChatEffort, string> = {
  low: 'Fastest, uses the least of your allowance',
  medium: 'Balanced depth and cost',
  high: 'Deepest reasoning, uses the most',
}

const KEY = 'algobuilder.chatEffort'

function isEffort(value: unknown): value is ChatEffort {
  return CHAT_EFFORTS.includes(value as ChatEffort)
}

export const effortStorage = {
  get(): ChatEffort {
    try {
      const saved = localStorage.getItem(KEY)
      return isEffort(saved) ? saved : DEFAULT_EFFORT
    } catch {
      return DEFAULT_EFFORT
    }
  },
  set(effort: ChatEffort): void {
    try {
      localStorage.setItem(KEY, effort)
    } catch {
      // storage unavailable — the selection just won't survive a refresh
    }
  },
}
